/**
 * Pure format functions: versioned Pixelwerkstatt JSON serialize/parse plus
 * compatibility parsing for the documented legacy formats (README.md):
 *  - legacy JSON: { width, height, pixels } with "#RRGGBB" strings or null
 *  - hex-grid text: one line per row, cells separated by "," or spaces,
 *    each cell "#RRGGBB", "transparent" or "-"
 *
 * No DOM dependency. All parse errors are PixelwerkstattError with precise,
 * user-presentable messages (German, matching the legacy app).
 */

import {
  MAX_DIMENSION,
  MAX_INPUT_CELLS,
  MAX_TEXT_LENGTH,
  MIN_DIMENSION,
} from "./limits.js";
import { PixelwerkstattError } from "./errors.js";
import { isValidHexColor, parseHexColor, rgba, type Rgba } from "./color.js";
import { PixelDocument } from "./document.js";

/** Current Pixelwerkstatt JSON format version. */
export const PIXELWERKSTATT_FORMAT_VERSION = 1;

export interface PixelwerkstattFile {
  format: "pixelwerkstatt";
  version: number;
  width: number;
  height: number;
  /**
   * Flat RGBA quadruples (length = width*height*4), same layout as
   * PixelDocument.data. Chosen over nested rows for exactness and speed.
   */
  data: number[];
}

const TRANSPARENT_TOKENS = new Set(["-", "transparent"]);
const CELL_SEPARATOR = ",";

// ---------------------------------------------------------------------------
// Shared matrix validation (used by both legacy formats)
// ---------------------------------------------------------------------------

interface ParsedMatrix {
  width: number;
  height: number;
  /** Row-major flat RGBA data, ready for PixelDocument.replaceWith. */
  data: Uint8ClampedArray;
}

function parsePixelToken(token: string, context: string): Rgba {
  const t = token.trim().toLowerCase();
  if (TRANSPARENT_TOKENS.has(t)) return rgba(0, 0, 0, 0);
  if (isValidHexColor(t)) return parseHexColor(t);
  throw new PixelwerkstattError(
    "PIXEL_TOKEN",
    `Ungültige Zelle '${token.trim()}' ${context}. Nutze #RRGGBB, transparent oder -.`,
    token,
  );
}

function parseCells(cells: string[], width: number, rowIndex: number, data: Uint8ClampedArray): void {
  if (cells.length !== width) {
    throw new PixelwerkstattError(
      "PIXELS_ROW_WIDTH",
      `Zeile ${rowIndex + 1} hat ${cells.length} Zellen, erwartet werden ${width}. Alle Zeilen müssen gleich viele Zellen haben.`,
    );
  }
  for (let x = 0; x < width; x++) {
    const color = parsePixelToken(cells[x]!, `in Zeile ${rowIndex + 1}`);
    const i = (rowIndex * width + x) * 4;
    data[i] = color.r;
    data[i + 1] = color.g;
    data[i + 2] = color.b;
    data[i + 3] = color.a;
  }
}

function assertDimensions(width: unknown, height: unknown): asserts width is number {
  if (typeof width !== "number" || !Number.isInteger(width) || typeof height !== "number" || !Number.isInteger(height)) {
    throw new PixelwerkstattError("DIMENSION_TYPE", "width und height müssen ganze Zahlen sein.");
  }
  if (
    width < MIN_DIMENSION || width > MAX_DIMENSION ||
    height < MIN_DIMENSION || height > MAX_DIMENSION
  ) {
    throw new PixelwerkstattError(
      "DIMENSION_RANGE",
      `width/height müssen zwischen ${MIN_DIMENSION} und ${MAX_DIMENSION} liegen (erhalten: ${width}×${height}).`,
    );
  }
}

/**
 * Guard against oversized raw text before any parsing work
 * (JSON.parse / line splitting). Exported constant MAX_TEXT_LENGTH is the
 * same limit the UI uses as a FileReader preflight.
 */
function assertTextSize(text: string): void {
  if (text.length > MAX_TEXT_LENGTH) {
    throw new PixelwerkstattError(
      "INPUT_TOO_LARGE",
      `Eingabe zu groß: ${text.length} Zeichen; maximal sind ${MAX_TEXT_LENGTH}.`,
    );
  }
}

// ---------------------------------------------------------------------------
// Hex-grid text (legacy, documented in README.md §1)
// ---------------------------------------------------------------------------

/**
 * Parse hex-grid text: one line per pixel row, cells separated by "," or
 * (when no comma is present) whitespace. Empty lines are skipped.
 */
export function parseHexGridText(text: string): ParsedMatrix {
  if (typeof text !== "string") {
    throw new PixelwerkstattError("INPUT_EMPTY", "Eingabe muss ein Text sein.");
  }
  assertTextSize(text);
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new PixelwerkstattError("INPUT_EMPTY", "Die Datei ist leer.");
  }
  if (trimmed.startsWith("{")) {
    throw new PixelwerkstattError(
      "UNKNOWN_FORMAT",
      "Die Eingabe sieht nach JSON aus. Nutze parsePixelwerkstattJson oder parseLegacyJson.",
    );
  }

  const lines = trimmed.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) {
    throw new PixelwerkstattError("INPUT_EMPTY", "Kein Hex-Raster gefunden.");
  }
  if (lines.length > MAX_DIMENSION) {
    throw new PixelwerkstattError(
      "DIMENSION_RANGE",
      `Zu viele Zeilen (${lines.length}); maximal sind ${MAX_DIMENSION}.`,
    );
  }

  const width = splitCells(lines[0]!).length;
  assertDimensions(width, lines.length);
  // Size guard from the PARSED cell count (not raw character length,
  // which over-counts separator-heavy grids), before any allocation.
  if (width * lines.length > MAX_INPUT_CELLS) {
    throw new PixelwerkstattError(
      "INPUT_TOO_LARGE",
      `Eingabe hat ${width * lines.length} Zellen; maximal sind ${MAX_INPUT_CELLS}.`,
    );
  }

  const data = new Uint8ClampedArray(width * lines.length * 4);
  for (let y = 0; y < lines.length; y++) {
    parseCells(splitCells(lines[y]!), width, y, data);
  }
  return { width, height: lines.length, data };
}

function splitCells(line: string): string[] {
  if (line.includes(CELL_SEPARATOR)) return line.split(CELL_SEPARATOR);
  // Fall back to whitespace separation (documented legacy behavior).
  return line.split(/\s+/).filter((c) => c.length > 0);
}

// ---------------------------------------------------------------------------
// Legacy JSON: { width, height, pixels } (documented in README.md §2)
// ---------------------------------------------------------------------------

/**
 * Parse a legacy {width,height,pixels} JSON text into a PixelDocument.
 */
export function parseLegacyJson(text: string): PixelDocument {
  return documentFromMatrix(parseLegacyJsonMatrix(text));
}

function parseLegacyJsonMatrix(text: string): ParsedMatrix {
  if (typeof text !== "string") {
    throw new PixelwerkstattError("INPUT_EMPTY", "Eingabe muss ein Text sein.");
  }
  assertTextSize(text);
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new PixelwerkstattError("INPUT_EMPTY", "Die Datei ist leer.");
  }
  let value: unknown;
  try {
    value = JSON.parse(trimmed);
  } catch (err) {
    throw new PixelwerkstattError(
      "JSON_PARSE",
      `JSON ist ungültig: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  return parseLegacyJsonObject(value);
}

/** Parse an already-decoded legacy {width,height,pixels} object. */
export function parseLegacyJsonObject(value: unknown): PixelDocument {
  return documentFromMatrix(parseLegacyJsonMatrixObject(value));
}

function parseLegacyJsonMatrixObject(value: unknown): ParsedMatrix {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new PixelwerkstattError("JSON_SHAPE", "JSON ist ungültig oder kein Objekt.");
  }
  const obj = value as Record<string, unknown>;
  const missing = ["width", "height", "pixels"].filter((k) => !(k in obj));
  if (missing.length > 0) {
    throw new PixelwerkstattError(
      "JSON_FIELDS",
      `JSON braucht width, height und pixels (fehlt: ${missing.join(", ")}).`,
    );
  }
  assertDimensions(obj.width, obj.height);
  const width = obj.width as number;
  const height = obj.height as number;

  const raw = obj.pixels;
  if (!Array.isArray(raw)) {
    throw new PixelwerkstattError("PIXELS_TYPE", "pixels muss ein Array sein.");
  }
  if (raw.length !== height) {
    throw new PixelwerkstattError(
      "PIXELS_ROW_COUNT",
      `pixels muss genau ${height} Zeilen enthalten (erhalten: ${raw.length}).`,
    );
  }
  if (width * height > MAX_INPUT_CELLS) {
    throw new PixelwerkstattError(
      "INPUT_TOO_LARGE",
      `Eingabe hat mehr als ${MAX_INPUT_CELLS} Zellen.`,
    );
  }

  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const row = raw[y];
    if (!Array.isArray(row)) {
      throw new PixelwerkstattError(
        "PIXELS_ROW_TYPE",
        `Zeile ${y + 1} von pixels muss ein Array sein.`,
      );
    }
    if (row.length !== width) {
      throw new PixelwerkstattError(
        "PIXELS_ROW_WIDTH",
        `Zeile ${y + 1} von pixels hat ${row.length} Werte, erwartet werden ${width}.`,
      );
    }
    for (let x = 0; x < width; x++) {
      const cell = row[x];
      const color =
        cell === null
          ? rgba(0, 0, 0, 0)
          : typeof cell === "string" && isValidHexColor(cell)
            ? parseHexColor(cell)
            : throwPixelError(cell, y + 1);
      const i = (y * width + x) * 4;
      data[i] = color.r;
      data[i + 1] = color.g;
      data[i + 2] = color.b;
      data[i + 3] = color.a;
    }
  }
  return { width, height, data };
}

function throwPixelError(cell: unknown, line: number): never {
  throw new PixelwerkstattError(
    "PIXEL_TOKEN",
    `JSON-Pixel müssen #RRGGBB-Strings oder null sein (Zeile ${line}, Wert: ${JSON.stringify(cell) ?? String(cell)}).`,
  );
}

// ---------------------------------------------------------------------------
// Versioned Pixelwerkstatt JSON (current format)
// ---------------------------------------------------------------------------

/** Serialize a document into the versioned Pixelwerkstatt JSON format. */
export function serializePixelwerkstattJson(doc: PixelDocument): string {
  const file: PixelwerkstattFile = {
    format: "pixelwerkstatt",
    version: PIXELWERKSTATT_FORMAT_VERSION,
    width: doc.width,
    height: doc.height,
    data: Array.from(doc.data),
  };
  return JSON.stringify(file);
}

/** Parse versioned Pixelwerkstatt JSON text into a PixelDocument. */
export function parsePixelwerkstattJson(text: string): PixelDocument {
  if (typeof text !== "string") {
    throw new PixelwerkstattError("INPUT_EMPTY", "Eingabe muss ein Text sein.");
  }
  assertTextSize(text);
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new PixelwerkstattError("INPUT_EMPTY", "Die Datei ist leer.");
  }
  let value: unknown;
  try {
    value = JSON.parse(trimmed);
  } catch (err) {
    throw new PixelwerkstattError(
      "JSON_PARSE",
      `JSON ist ungültig: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  return pixelwerkstattJsonFromObject(value);
}

/** Parse an already-decoded versioned Pixelwerkstatt JSON object. */
export function pixelwerkstattJsonFromObject(value: unknown): PixelDocument {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new PixelwerkstattError("JSON_SHAPE", "JSON ist ungültig oder kein Objekt.");
  }
  const obj = value as Record<string, unknown>;
  if (obj.format !== "pixelwerkstatt") {
    throw new PixelwerkstattError(
      "JSON_VERSION_MISSING",
      'Datei hat kein "format": "pixelwerkstatt" – keine Pixelwerkstatt-Datei?',
    );
  }
  if (obj.version === undefined) {
    throw new PixelwerkstattError("JSON_VERSION_MISSING", 'Datei hat keine "version".');
  }
  if (obj.version !== PIXELWERKSTATT_FORMAT_VERSION) {
    throw new PixelwerkstattError(
      "JSON_VERSION",
      `Nicht unterstützte Format-Version ${JSON.stringify(obj.version)}; unterstützt wird ${PIXELWERKSTATT_FORMAT_VERSION}.`,
    );
  }
  const missing = ["width", "height", "data"].filter((k) => !(k in obj));
  if (missing.length > 0) {
    throw new PixelwerkstattError(
      "JSON_FIELDS",
      `Datei braucht width, height und data (fehlt: ${missing.join(", ")}).`,
    );
  }
  assertDimensions(obj.width, obj.height);
  const width = obj.width as number;
  const height = obj.height as number;
  const raw = obj.data;
  if (!Array.isArray(raw)) {
    throw new PixelwerkstattError("PIXELS_TYPE", "data muss ein Array sein.");
  }
  const expected = width * height * 4;
  if (raw.length !== expected) {
    throw new PixelwerkstattError(
      "PIXELS_ROW_WIDTH",
      `data hat ${raw.length} Werte, erwartet werden ${expected} (width*height*4).`,
    );
  }
  const data = new Uint8ClampedArray(expected);
  for (let i = 0; i < expected; i++) {
    const channel = raw[i];
    if (typeof channel !== "number" || !Number.isInteger(channel) || channel < 0 || channel > 255) {
      throw new PixelwerkstattError(
        "COLOR_COMPONENT_RANGE",
        `data[${i}] muss eine ganze Zahl zwischen 0 und 255 sein (erhalten: ${JSON.stringify(channel) ?? String(channel)}).`,
      );
    }
    data[i] = channel;
  }
  return PixelDocument.create(width, height, data);
}

/**
 * Auto-detect the documented import formats, in this order:
 *  1. versioned Pixelwerkstatt JSON
 *  2. legacy {width,height,pixels} JSON
 *  3. hex-grid text
 */
export function parseAnyText(text: string): PixelDocument {
  if (typeof text !== "string") {
    throw new PixelwerkstattError("INPUT_EMPTY", "Eingabe muss ein Text sein.");
  }
  assertTextSize(text);
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new PixelwerkstattError("INPUT_EMPTY", "Die Datei ist leer.");
  }
  if (trimmed.startsWith("{")) {
    let value: unknown;
    try {
      value = JSON.parse(trimmed);
    } catch {
      throw new PixelwerkstattError("JSON_PARSE", "JSON ist ungültig.");
    }
    if (
      typeof value === "object" && value !== null && !Array.isArray(value) &&
      (value as Record<string, unknown>).format === "pixelwerkstatt"
    ) {
      return pixelwerkstattJsonFromObject(value);
    }
    return documentFromMatrix(parseLegacyJsonMatrixObject(value));
  }
  return documentFromMatrix(parseHexGridText(text));
}

/** Apply a validated matrix (from legacy parsers) to a fresh document. */
export function documentFromMatrix(matrix: ParsedMatrix): PixelDocument {
  return PixelDocument.create(matrix.width, matrix.height, matrix.data);
}

export type { ParsedMatrix };
